import React, { forwardRef } from 'react';
import { LayoutChangeEvent, Platform, StyleProp, TextStyle, Text as RNText } from 'react-native';
import { colors } from '../../utils/theme';
import { FontWeight, fontWeightToFontFamilyName } from '../../hooks/useLoadAssets';
import getBrowser from '../../utils/getBrowser';

export type TextVariant =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'bold-h4'
    | 'bold-h6'
    | 'bold-h5'
    | 'active-tab'
    | 'button'
    | 'card-title'
    | 'inactive-tab'
    | 'body-1'
    | 'bold-body-1'
    | 'card-body'
    | 'card-support'
    | 'input-field'
    | 'input-text'
    | 'input-label'
    | 'subtitle-1'
    | 'subtitle-2'
    | 'body-2'
    | 'caption-1'
    | 'bold-caption-1'
    | 'caption-2'
    | 'select-option'
    | 'selected-select-option'
    | 'input-error'
    | 'input-help-text'
    | 'input-warn-text'
    | 'avatar-fallback-xs'
    | 'avatar-fallback-sm'
    | 'avatar-fallback-md'
    | 'placeholder-text'
    | 'large-placeholder-text'
    | 'text-button-text'
    | 'labeled-value-label'
    | 'labeled-value-value'
    | 'note-title-value'
    | 'note-title-placeholder'
    | 'note-content-value'
    | 'note-content-placeholder'
    | 'header-breadcrumb-parent'
    | 'header-breadcrumb-leaf'
    | 'header-breadcrumb-divider'
    | 'profile-header-text'
    | 'header-breadcrumb-parent-non-clickable'
    | 'empty-state-hint-primary-sub-text'
    | 'empty-state-hint-header'
    | 'notification-title'
    | 'table-header'
    | 'table-content'
    | 'tab-text'
    | 'model-processing-dot'
    | 'profiles-section-header'
    | 'permissions-primary-header'
    | 'permissions-secondary-header'
    | 'micro'
    | 'base-header-title'
    | 'ai-search-input-placeholder-text'
    | 'ai-search-input-text';

type GetStyleArgs = {
    size: number;
    lineHeightMultiple?: number;
    letterSpacing?: number;
    color?:
        | 'Dark-Text'
        | 'Gray-Text'
        | 'Inactive-Text'
        | 'Alert'
        | 'White'
        | 'Grey'
        | 'Dark'
        | 'Grey-Light'
        | 'Primary'
        | 'Primary-Light'
        | 'Text-Link';
    bold?: 'Reg' | 'SemiBold' | 'Med';
};

type TextStyleProps = {
    fontSize: number;
    fontFamily: string;
    fontWeight: '400' | '500' | '600';
    lineHeight: number;
    letterSpacing: number;
    color: string;
};

const getStyleProps = ({
    size,
    lineHeightMultiple = 1.3,
    letterSpacing = -0.05,
    color = 'Dark-Text',
    bold = 'Reg',
}: GetStyleArgs): TextStyleProps => {
    const loadedFontWeight: FontWeight = bold === 'Reg' ? 'regular' : bold === 'Med' ? 'medium' : 'semi-bold';
    return {
        fontSize: size,
        fontFamily: fontWeightToFontFamilyName.Overpass[loadedFontWeight],
        fontWeight: (
            {
                Reg: '400',
                Med: '500',
                SemiBold: '600',
            } as const
        )[bold],
        lineHeight: size * (lineHeightMultiple || 1.3),
        letterSpacing: letterSpacing,
        color: colors[color],
    };
};

export type TextColor =
    | 'Dark-Text'
    | 'Gray-Text'
    | 'Light-Text'
    | 'Inactive-Text'
    | 'White-Text'
    | 'Alert'
    | 'White'
    | 'Dark'
    | 'Primary-Button-Default'
    | 'Primary-Button-Hover'
    | 'Primary-Button-Press'
    | 'Grey-Light'
    | 'Primary-Light'
    | 'Success'
    | 'Text-Link'
    | 'Primary'
    | 'Primary-Reg'
    | 'Grey';

const ellipsizeTextVariants: TextVariant[] = ['button', 'card-support', 'text-button-text', 'base-header-title'];

export const textVariantStyleMap: Record<TextVariant, TextStyleProps> = {
    h1: getStyleProps({ size: 34, bold: 'SemiBold' }),
    h2: getStyleProps({ size: 28, bold: 'SemiBold' }),
    h3: getStyleProps({ size: 22, bold: 'SemiBold' }),
    h4: getStyleProps({ size: 18 }),
    h5: getStyleProps({ size: 16, letterSpacing: 0 }),
    h6: getStyleProps({ size: 14, letterSpacing: 0 }),
    'bold-h6': getStyleProps({ size: 14, letterSpacing: 0, bold: 'SemiBold' }),
    'bold-h4': getStyleProps({ size: 18, bold: 'SemiBold' }),
    'bold-h5': getStyleProps({ size: 16, letterSpacing: 0, bold: 'SemiBold' }),
    'permissions-primary-header': getStyleProps({ size: 18, bold: 'SemiBold', color: 'Dark-Text' }),
    'permissions-secondary-header': getStyleProps({ size: 14, bold: 'SemiBold', color: 'Dark-Text' }),
    'profiles-section-header': getStyleProps({ size: 16, bold: 'SemiBold', color: 'Dark-Text' }),
    'model-processing-dot': getStyleProps({ size: 34, bold: 'SemiBold', lineHeightMultiple: 0.5 }),
    'table-header': getStyleProps({ size: 14, bold: 'SemiBold', color: 'Dark-Text' }),
    'table-content': getStyleProps({ size: 14, color: 'Dark-Text' }),
    'empty-state-hint-header': getStyleProps({ size: 18, letterSpacing: -0.5, color: 'Gray-Text' }),
    'empty-state-hint-primary-sub-text': getStyleProps({ size: 18, letterSpacing: -0.5, color: 'Dark-Text' }),
    'profile-header-text': getStyleProps({ size: 18, letterSpacing: 0, color: 'Dark-Text', bold: 'SemiBold' }),
    'header-breadcrumb-parent': getStyleProps({
        size: 16,
        lineHeightMultiple: 1.2,
        letterSpacing: 0,
        color: 'Text-Link',
        bold: 'SemiBold',
    }),
    'header-breadcrumb-parent-non-clickable': getStyleProps({
        size: 16,
        lineHeightMultiple: 1.2,
        letterSpacing: 0,
        color: 'Gray-Text',
        bold: 'SemiBold',
    }),
    'header-breadcrumb-leaf': getStyleProps({
        size: 16,
        lineHeightMultiple: 1.2,
        letterSpacing: 0,
        color: 'White',
        bold: 'SemiBold',
    }),
    'header-breadcrumb-divider': getStyleProps({
        size: 16,
        lineHeightMultiple: 1.2,
        letterSpacing: 0,
        color: 'Gray-Text',
        bold: 'SemiBold',
    }),
    'note-title-value': getStyleProps({ size: 18, letterSpacing: 0, color: 'Dark-Text', bold: 'SemiBold' }),
    'note-title-placeholder': getStyleProps({ size: 18, letterSpacing: 0, color: 'Grey-Light' }),
    'note-content-value': getStyleProps({ size: 14, letterSpacing: 0, color: 'Dark-Text' }),
    'note-content-placeholder': getStyleProps({ size: 14, letterSpacing: 0, color: 'Grey-Light' }),
    'text-button-text': getStyleProps({ size: 14, bold: 'SemiBold', letterSpacing: 0 }),
    'active-tab': getStyleProps({ size: 14, bold: 'SemiBold', letterSpacing: 0 }),
    button: getStyleProps({ size: 14, bold: 'SemiBold', letterSpacing: 0 }),
    'card-title': getStyleProps({ size: 14, bold: 'SemiBold', letterSpacing: 0 }),
    'inactive-tab': getStyleProps({ size: 14, color: 'Gray-Text' }),
    'body-1': getStyleProps({ size: 14, letterSpacing: 0 }),
    'notification-title': getStyleProps({ size: 14, color: 'White', letterSpacing: 0 }),
    'bold-body-1': getStyleProps({ size: 14, bold: 'SemiBold', letterSpacing: 0 }),
    'input-text': getStyleProps({ size: 14, color: 'Dark-Text', bold: 'SemiBold', letterSpacing: 0 }),
    'placeholder-text': getStyleProps({ size: 14, color: 'Gray-Text', letterSpacing: 0 }),
    'large-placeholder-text': getStyleProps({ size: 18, color: 'Gray-Text', letterSpacing: 0 }),
    'card-body': getStyleProps({ size: 14, color: 'Gray-Text', letterSpacing: 0 }),
    'input-field': getStyleProps({ size: 14, color: 'Gray-Text', letterSpacing: 0 }),
    'subtitle-1': getStyleProps({ size: 16, bold: 'SemiBold', letterSpacing: 0 }),
    'subtitle-2': getStyleProps({ size: 14, bold: 'SemiBold', letterSpacing: 0 }),
    'body-2': getStyleProps({ size: 12, letterSpacing: 0 }),
    'card-support': getStyleProps({ size: 12, color: 'Gray-Text', bold: 'Med', letterSpacing: 0 }),
    'caption-1': getStyleProps({ size: 12, letterSpacing: 0.5 }),
    'bold-caption-1': getStyleProps({ size: 12, letterSpacing: 0.5, bold: 'SemiBold' }),
    'input-label': getStyleProps({ size: 10, color: 'Gray-Text', letterSpacing: 0.5, bold: 'SemiBold' }),
    'caption-2': getStyleProps({ size: 12, letterSpacing: 0.5 }),
    'tab-text': getStyleProps({ size: 12, letterSpacing: 0.5 }),
    'select-option': getStyleProps({ size: 16, letterSpacing: 0, color: 'Gray-Text' }),
    'selected-select-option': getStyleProps({ size: 16, bold: 'SemiBold', color: 'Dark-Text', letterSpacing: 0 }),
    'input-error': getStyleProps({ size: 12, color: 'Alert' }),
    'input-help-text': getStyleProps({ size: 12, color: 'Gray-Text' }),
    'input-warn-text': getStyleProps({ size: 12, color: 'Dark-Text', bold: 'SemiBold' }),
    'avatar-fallback-xs': getStyleProps({ size: 12, bold: 'SemiBold', color: 'Dark-Text' }),
    'avatar-fallback-sm': getStyleProps({ size: 14, bold: 'SemiBold', color: 'White' }),
    'avatar-fallback-md': getStyleProps({ size: 18, bold: 'SemiBold', color: 'White' }),
    'labeled-value-label': getStyleProps({ size: 12, color: 'Gray-Text', bold: 'SemiBold', letterSpacing: 0 }),
    'labeled-value-value': getStyleProps({ size: 14, color: 'Dark-Text', letterSpacing: 0 }),
    micro: getStyleProps({ size: 7, color: 'Gray-Text', letterSpacing: 0 }),
    'base-header-title': getStyleProps({ size: 18 }),
    'ai-search-input-placeholder-text': getStyleProps({ size: 22, color: 'Gray-Text', letterSpacing: 0 }),
    'ai-search-input-text': getStyleProps({ size: 22, color: 'Dark-Text', bold: 'SemiBold', letterSpacing: 0 }),
};

export type TextOwnProps = {
    variant: TextVariant;
    children: string | number | undefined | null | React.ReactNode;
    color?: TextColor;
    onLayout?: (event: LayoutChangeEvent) => void;
    style?: StyleProp<TextStyle>;
    alignText?: 'center' | 'left' | 'right';
    wrapText?: boolean;
    testID?: string;
    underline?: boolean;
};

export type TextProps = TextOwnProps;

export const getTextStyle = (variant: TextVariant): StyleProp<TextStyle> => {
    return textVariantStyleMap[variant];
};

const Text: React.FC<TextProps> = forwardRef((props, ref) => {
    if (!props.children) return null;
    const browser = getBrowser(navigator.userAgent);

    const useEllipses = ellipsizeTextVariants.includes(props.variant);
    const numberOfLines = props.wrapText === false || useEllipses ? 1 : undefined;
    // selectable breaks ellipses on android
    const selectable = Platform.OS === 'web' || (Platform.OS === 'android' && useEllipses) ? undefined : true;

    return (
        <RNText
            style={[
                getTextStyle(props.variant),
                {
                    color: props.color ? colors[props.color] : textVariantStyleMap[props.variant].color,
                },
                {
                    textAlign: props.alignText,
                },
                {
                    flexWrap: props.wrapText === true ? 'wrap' : undefined,
                },
                {
                    textDecorationLine: props.underline ? 'underline' : 'none',
                },
                {
                    fontSynthesis: browser === 'Safari' ? 'style' : undefined,
                } as any,
                props.style,
            ]}
            onLayout={props.onLayout}
            ref={ref as any}
            testID={props.testID}
            selectable={selectable}
            numberOfLines={numberOfLines}
            allowFontScaling={true}
            ellipsizeMode={useEllipses ? 'tail' : undefined}
        >
            {props.children}
        </RNText>
    );
});

export default React.memo(Text);
